// GenerateForm - Form for generating new AI content

import { useState } from 'react';
import { useGenerateContent } from '../../../hooks/unified/useContentVersionsQueries';
import { useToast } from '../../../contexts/ToastContext';
import type { ContentComponentType } from '../../../hooks/unified/useContentVersionsQueries';

interface GenerateFormProps {
  jobId: string;
  componentType: ContentComponentType;
  onSuccess?: (newContent: string | string[]) => void;
}

type ToneType = 'professional' | 'casual' | 'technical';

export function GenerateForm({ jobId, componentType, onSuccess }: GenerateFormProps) {
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState<ToneType>('professional');
  const { showSuccess, showError } = useToast();
  const generateMutation = useGenerateContent();

  const handleGenerate = async () => {
    try {
      const result = await generateMutation.mutateAsync({
        jobId,
        componentType,
        request: {
          prompt: prompt.trim() || undefined,
          tone,
        },
      });

      showSuccess('Content Generated', 'New content has been generated and activated.');
      
      // Parse content for arrays (requirements/benefits)
      let parsedContent: string | string[] = result.version.content;
      if (componentType === 'requirements' || componentType === 'benefits') {
        try {
          parsedContent = JSON.parse(result.version.content);
        } catch {
          // Keep as string if not valid JSON
        }
      }
      
      onSuccess?.(parsedContent);
      setPrompt('');
    } catch (error) {
      showError('Generation Failed', error instanceof Error ? error.message : 'Failed to generate content');
    }
  };

  const placeholders: Record<ContentComponentType, string> = {
    title: 'e.g., Make it more specific to senior roles...',
    summary: 'e.g., Make it more engaging, highlight key benefits...',
    description: 'e.g., Focus on remote work benefits, emphasize growth opportunities...',
    requirements: 'e.g., Add cloud experience requirements, focus on leadership skills...',
    benefits: 'e.g., Highlight work-life balance, add learning opportunities...',
    image: 'e.g., Modern office with collaborative workspace...',
  };

  return (
    <div className="space-y-4">
      {/* Custom Prompt */}
      <div>
        <label className="block text-sm text-gray-600 mb-1">
          Custom Instructions (optional)
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={placeholders[componentType]}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm 
                     focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                     placeholder:text-gray-400"
        />
      </div>

      {/* Tone Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <label className="text-sm text-gray-600">Tone:</label>
        <div className="flex gap-2 overflow-x-auto mobile-scroll pb-1 sm:pb-0">
          {(['professional', 'casual', 'technical'] as ToneType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTone(t)}
              className={`flex-shrink-0 px-3 py-2 text-sm rounded-lg border transition-colors min-h-[44px] ${
                tone === t
                  ? 'bg-purple-100 border-purple-300 text-purple-700'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={generateMutation.isPending}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 
                   bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400
                   text-white font-medium rounded-lg transition-colors min-h-[48px]"
      >
        {generateMutation.isPending ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Generating...</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            <span>Generate with AI</span>
          </>
        )}
      </button>
    </div>
  );
}
